import { createContext, forwardRef, useContext } from "react";
import type {
  HTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
  ReactNode,
} from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp } from "lucide-react";
import { cn } from "../lib/cn";
import { useUiDensity } from "../hooks/useDensity";

export type TableDensity = "dense" | "comfortable";

const DensityContext = createContext<TableDensity>("dense");

/**
 * Altura de linha: 44px no denso (padrão) e 56px no confortável. Nunca
 * abaixo de 44 — é o alvo de toque mínimo no celular (seções 4 e 11).
 */
const ROW_HEIGHT: Record<TableDensity, string> = {
  dense: "h-row-dense",
  comfortable: "h-row",
};

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  /** Descrição da tabela para leitor de tela. Pode ficar visualmente oculta. */
  caption?: ReactNode;
  /** `false` mostra a caption na tela. */
  captionHidden?: boolean;
  density?: TableDensity;
  /** Zebra — recomendada acima de 8 colunas. */
  zebra?: boolean;
  /** Classe do container que rola na horizontal. */
  containerClassName?: string;
}

/**
 * `<table>` de verdade (não divs): é o que dá navegação por célula no leitor
 * de tela. Header sticky, scroll horizontal no mobile — nunca comprimir
 * coluna até ficar ilegível (seção 11).
 */
export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
  {
    className,
    containerClassName,
    caption,
    captionHidden = true,
    density: densityProp = "dense",
    zebra = false,
    children,
    ...props
  },
  ref,
) {
  // I3: com o painel em "compacto", toda tabela usa a linha de 44 px — mesmo a que pediu
  // a confortável. O inverso não vale: tabela densa por desenho continua densa.
  const uiDensity = useUiDensity();
  const density = uiDensity === "compact" ? "dense" : densityProp;
  return (
    <DensityContext.Provider value={density}>
      <div
        className={cn(
          // painel branco sobre o canvas: sem borda, quem separa é o contraste
          "relative w-full overflow-x-auto overscroll-x-contain rounded-lg bg-surface scrollbar-thin",
          containerClassName,
        )}
      >
        <table
          ref={ref}
          data-density={density}
          data-zebra={zebra || undefined}
          className={cn(
            "w-full border-collapse text-left text-sm",
            // 20px de respiro na primeira e na última coluna, como o painel
            "[&_tr>*:first-child]:pl-5 [&_tr>*:last-child]:pr-5",
            zebra && "[&_tbody_tr:nth-child(even)]:bg-surface-subtle",
            className,
          )}
          {...props}
        >
          {caption ? (
            <caption
              className={cn(
                captionHidden ? "sr-only" : "px-3 py-2 text-left text-xs text-muted-foreground",
              )}
            >
              {caption}
            </caption>
          ) : null}
          {children}
        </table>
      </div>
    </DensityContext.Provider>
  );
});

export function TableHeader({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={cn(
        // cabeçalho cinza (surface-subtle), uma linha fina embaixo
        "sticky top-0 z-10 bg-surface-subtle [&_tr]:border-b [&_tr]:border-border",
        className,
      )}
      {...props}
    />
  );
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

export function TableFooter({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tfoot
      // linha de total: tingida e em semibold, sem borda dupla
      className={cn("border-t border-border bg-surface-subtle font-semibold", className)}
      {...props}
    />
  );
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Linha inteira clicável — a área de toque é a LINHA, não só o texto. */
  interactive?: boolean;
  selected?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(function TableRow(
  { className, interactive = false, selected = false, ...props },
  ref,
) {
  const density = useContext(DensityContext);
  return (
    <tr
      ref={ref}
      aria-selected={selected || undefined}
      tabIndex={interactive ? 0 : undefined}
      className={cn(
        ROW_HEIGHT[density],
        "border-b border-border transition-colors duration-100",
        "hover:bg-surface-subtle",
        selected && "bg-primary-soft hover:bg-primary-soft",
        interactive &&
          "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        className,
      )}
      {...props}
    />
  );
});

export type SortDirection = "asc" | "desc" | null;

export interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Coluna de número: alinha à direita e liga tabular-nums no header também. */
  numeric?: boolean;
  sortable?: boolean;
  /** `null` = ordenável mas não ordenada agora. */
  sortDirection?: SortDirection;
  onSort?: () => void;
  /** Coluna de ação presa à direita em tabela larga no mobile. */
  sticky?: "right";
}

export function TableHead({
  className,
  numeric = false,
  sortable = false,
  sortDirection = null,
  onSort,
  sticky,
  children,
  ...props
}: TableHeadProps) {
  const ariaSort = sortable
    ? sortDirection === "asc"
      ? "ascending"
      : sortDirection === "desc"
        ? "descending"
        : "none"
    : undefined;

  const SortIcon =
    sortDirection === "asc" ? ChevronUp : sortDirection === "desc" ? ChevronDown : ChevronsUpDown;

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      className={cn(
        "h-10 px-3 text-xs font-medium whitespace-nowrap text-subtle-foreground",
        numeric ? "text-right nums" : "text-left",
        sticky === "right" && "sticky right-0 z-20 bg-surface-subtle",
        sortDirection && "text-foreground",
        className,
      )}
      {...props}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className={cn(
            "inline-flex items-center gap-1 rounded-xs",
            "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            numeric && "flex-row-reverse",
          )}
        >
          <span>{children}</span>
          <SortIcon
            aria-hidden="true"
            className={cn(
              "size-3 shrink-0",
              sortDirection === null ? "opacity-50" : "text-primary-text",
            )}
          />
        </button>
      ) : (
        children
      )}
    </th>
  );
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean;
  sticky?: "right";
  /** Célula que não pode quebrar (data, status). */
  nowrap?: boolean;
}

export function TableCell({
  className,
  numeric = false,
  sticky,
  nowrap = false,
  ...props
}: TableCellProps) {
  return (
    <td
      className={cn(
        "px-3 align-middle text-sm",
        numeric && "nums text-right",
        nowrap && "whitespace-nowrap",
        sticky === "right" && "sticky right-0 z-10 bg-surface",
        className,
      )}
      {...props}
    />
  );
}

/** Cabeçalho de linha (primeira coluna) — `scope="row"` para o leitor de tela. */
export function TableRowHeader({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="row"
      className={cn("px-3 text-left align-middle text-sm font-medium", className)}
      {...props}
    />
  );
}

export interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  density?: TableDensity;
}

/**
 * Skeleton no formato EXATO da tabela final (mesma altura de linha,
 * mesmo número de colunas) — seção 1.6.
 */
export function TableSkeleton({ rows = 6, columns = 4, density = "dense" }: TableSkeletonProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="w-full overflow-hidden rounded-lg bg-surface"
    >
      <span className="sr-only">Carregando tabela</span>
      {/* o cabeçalho cinza também aparece no esqueleto: mesmo formato da final */}
      <div aria-hidden="true" className="flex h-10 items-center gap-3 border-b border-border bg-surface-subtle px-5">
        {Array.from({ length: columns }, (_, col) => (
          <div key={col} className={cn("h-2 rounded-sm bg-muted", col === 0 ? "w-1/5" : "flex-1")} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, row) => (
        <div
          key={row}
          className={cn(
            "flex items-center gap-3 border-b border-border px-5 last:border-0",
            ROW_HEIGHT[density],
          )}
        >
          {Array.from({ length: columns }, (_, col) => (
            <div
              key={col}
              className={cn(
                "h-3 animate-pulse rounded-sm bg-muted motion-reduce:animate-none",
                col === 0 ? "w-2/5" : "flex-1",
              )}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export interface TableEntityProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** O nome da coisa (grupo, campanha, monitorador). */
  title: ReactNode;
  /** O ID/código em mono, embaixo. */
  sub?: ReactNode;
}

/** Nome em cima, ID mono embaixo — a célula primária de toda linha. */
export function TableEntity({ title, sub, className, ...props }: TableEntityProps) {
  return (
    <div className={cn("min-w-0", className)} {...props}>
      <div className="truncate font-medium text-foreground">{title}</div>
      {sub ? (
        <div className="mt-0.5 truncate font-mono text-2xs text-subtle-foreground">{sub}</div>
      ) : null}
    </div>
  );
}

export interface PaginationProps extends Omit<HTMLAttributes<HTMLElement>, "onChange"> {
  /** Página atual, começando em 1. */
  page: number;
  /** Total de páginas. */
  pages: number;
  onPageChange: (page: number) => void;
  /** Acima disso, as páginas do meio viram "…". Padrão 7. */
  maxButtons?: number;
  previousLabel?: string;
  nextLabel?: string;
}

/** Lista de páginas a mostrar, com `null` no lugar das reticências. */
function pageWindow(page: number, pages: number, maxButtons: number): Array<number | null> {
  if (pages <= maxButtons) return Array.from({ length: pages }, (_, i) => i + 1);
  const side = Math.max(1, Math.floor((maxButtons - 3) / 2));
  const start = Math.max(2, page - side);
  const end = Math.min(pages - 1, page + side);
  const out: Array<number | null> = [1];
  if (start > 2) out.push(null);
  for (let p = start; p <= end; p += 1) out.push(p);
  if (end < pages - 1) out.push(null);
  out.push(pages);
  return out;
}

const PAGER_BUTTON = cn(
  "inline-flex h-8 min-w-8 items-center justify-center gap-1 rounded-pill px-2.5 text-xs font-medium nums",
  "text-foreground hover:bg-muted",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  "disabled:cursor-default disabled:bg-transparent disabled:text-subtle-foreground",
  "max-md:min-h-11 max-md:min-w-11",
);

/**
 * Paginador de rodapé de tabela: botões em cápsula, página atual em tinta
 * inversa, anterior/próxima desabilitados nas pontas (nunca somem).
 */
export function Pagination({
  page,
  pages,
  onPageChange,
  maxButtons = 7,
  previousLabel = "Anterior",
  nextLabel = "Próxima",
  className,
  ...props
}: PaginationProps) {
  const total = Math.max(1, pages);
  const current = Math.min(total, Math.max(1, page));
  const go = (p: number) => onPageChange(Math.min(total, Math.max(1, p)));

  return (
    <nav aria-label="Paginação" className={cn("flex items-center gap-1", className)} {...props}>
      <button
        type="button"
        className={PAGER_BUTTON}
        disabled={current <= 1}
        onClick={() => go(current - 1)}
      >
        <ChevronLeft aria-hidden="true" className="size-3.5" />
        <span className="max-sm:sr-only">{previousLabel}</span>
      </button>
      {pageWindow(current, total, maxButtons).map((p, i) =>
        p === null ? (
          <span key={`gap-${i}`} aria-hidden="true" className="px-1 text-xs text-subtle-foreground">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            aria-current={p === current ? "page" : undefined}
            aria-label={`Página ${p}`}
            onClick={() => go(p)}
            className={cn(
              PAGER_BUTTON,
              p === current && "bg-surface-inverse text-inverse-foreground hover:bg-surface-inverse",
            )}
          >
            {p}
          </button>
        ),
      )}
      <button
        type="button"
        className={PAGER_BUTTON}
        disabled={current >= total}
        onClick={() => go(current + 1)}
      >
        <span className="max-sm:sr-only">{nextLabel}</span>
        <ChevronRight aria-hidden="true" className="size-3.5" />
      </button>
    </nav>
  );
}
